import { webSocketClient } from "@/libs/api/services/WebSocketService";
import { ChatMessageWithSender } from "@/libs/api/types";
import { useCallback, useEffect } from "react";
import { useChatContext } from "./useChatContext";

export function useChatEvents() {
  const { setContext, currentConversation, conversations } = useChatContext();

  const sendMessage = useCallback(
    (message: string, messageType: string, conversationId: string) => {
      const socket = webSocketClient.getSocket("chats");
      if (!socket) {
        return;
      }

      socket.emit("message:send", {
        messageType: messageType,
        conversationId: conversationId,
        content: message,
      });
    },
    [],
  );

  const markAsRead = useCallback(
    (messageId: string, conversationId: string) => {
      if (
        !currentConversation ||
        currentConversation.conversationId !== conversationId
      ) {
        return;
      }

      const message = currentConversation.messages.find(
        (msg) => msg.id === messageId,
      );

      if (
        !message ||
        message.isRead ||
        message.senderId === currentConversation.senderId
      ) {
        return;
      }

      const socket = webSocketClient.getSocket("chats");
      if (!socket) {
        return;
      }

      socket.emit("message:read", {
        messageId,
        conversationId,
      });

      setContext({
        type: "markMessageAsRead",
        payload: {
          messageId,
          conversationId,
        },
      });

      const conversation = conversations.get(conversationId);
      if (
        !conversation ||
        !conversation.unreadCount ||
        conversation.unreadCount <= 0
      ) {
        return;
      }

      setContext({
        type: "updateConversation",
        payload: {
          conversationId,
          updates: { unreadCount: conversation.unreadCount - 1 },
        },
      });
    },
    [currentConversation, conversations, setContext],
  );

  useEffect(() => {
    const socket = webSocketClient.getSocket("chats");
    if (!socket) {
      return;
    }

    const handleNewMessage = (message: ChatMessageWithSender) => {
      if (!currentConversation) {
        return;
      }

      const isCurrentConversation =
        currentConversation.conversationId === message.conversationId;

      setContext({ type: "addMessage", payload: message });

      if (isCurrentConversation) {
        return;
      }

      const conversation = conversations.get(message.conversationId);
      if (!conversation) {
        return;
      }

      const currentUnreadCount = conversation.unreadCount ?? 0;
      setContext({
        type: "updateConversation",
        payload: {
          conversationId: message.conversationId,
          updates: {
            unreadCount: currentUnreadCount + 1,
            lastMessage: message,
          },
        },
      });
    };

    const handleMessageRead = (data: {
      messageId: string;
      conversationId: string;
    }) => {
      setContext({
        type: "markMessageAsRead",
        payload: {
          messageId: data.messageId,
          conversationId: data.conversationId,
        },
      });
    };

    socket.on("message:new", handleNewMessage);
    socket.on("message:read", handleMessageRead);

    return () => {
      socket.off("message:new", handleNewMessage);
      socket.off("message:read", handleMessageRead);
    };
  }, [setContext, currentConversation, conversations]);

  return {
    sendMessage,
    markAsRead,
  };
}
